import { useEffect, useState } from "react";
import axios from "axios";
import { API, useAuth } from "../App";
import { isPresidentRole } from "./RoleScopeBanner";
import { AlertTriangle, Loader2, Users, XCircle } from "lucide-react";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";

const normName = (s) => String(s || "").normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/\s+/g, " ").trim().toLowerCase();

// Χάρτης όνομα → μέλος (object) για σήμανση σύγκρουσης Μονομελούς / εξαίρεσης
const memberMap = (c) => {
  const map = {};
  (c?.members || []).forEach(m => { if (m && typeof m === "object" && m.name) map[normName(m.name)] = m; });
  return map;
};

export const compositionSummary = (c) => {
  if (!c) return "";
  const parts = [];
  if (c.president) parts.push(`Πρόεδρος: ${c.president}`);
  const others = (c.regular || []).filter(n => n !== c.president);
  if (others.length) parts.push(`Μέλη: ${others.join(", ")}`);
  if ((c.alternate || []).length) parts.push(`Αναπλ.: ${c.alternate.join(", ")}`);
  if ((c.excluded || []).length) parts.push(`Εξαιρέθηκαν (Μονομελές ίδια ημέρα): ${c.excluded.join(", ")}`);
  return parts.join(" · ");
};

export const sectorLabel = (c) => {
  if (!c?.sector_number) return "";
  return `${c.sector_number}ο Τμήμα${c.sector_title ? ` · ${c.sector_title}` : ""}`;
};

// Όνομα μέλους με χρωματική επισήμανση: κόκκινο + σημείωση όταν έχει δικάσιμο Μονομελούς την ίδια ημέρα
const MemberName = ({ name, m }) => {
  if (m?.mono_conflict) {
    return (
      <span className="text-red-700 font-semibold" title={`Δικάσιμος Μονομελούς την ίδια ημέρα: ${m.mono_conflict}`}
            data-testid={`member-conflict-${name}`}>
        <AlertTriangle className="inline w-3 h-3 mr-0.5 -mt-0.5" />{name}
        <span className="font-normal text-[11px]"> — Μονομελές ίδια ημέρα: {m.mono_conflict}</span>
      </span>
    );
  }
  return <span className="font-medium">{name}</span>;
};

const MemberLine = ({ title, names, map, testId }) => {
  if (!names?.length) return null;
  return (
    <span className="block text-xs leading-snug text-slate-800 whitespace-normal" data-testid={testId}>
      <span className="text-slate-600">{title}: </span>
      {names.map((n, i) => <span key={n}>{i > 0 && ", "}<MemberName name={n} m={map[normName(n)]} /></span>)}
    </span>
  );
};

// Πλήρης εμφάνιση σύνθεσης: τμήμα + Πρόεδρος/Τακτικά/Αναπληρωματικά (+ εξαιρεθέντες για τη δικάσιμο)
export const CompositionDetails = ({ c, testId }) => {
  if (!c) return null;
  const map = memberMap(c);
  const others = (c.regular || []).filter(n => n !== c.president);
  return (
    <span className="block" data-testid={testId}>
      <span className="block text-sm font-semibold text-slate-900 leading-snug">
        {c.label}
        {sectorLabel(c) && <span className="font-normal text-violet-800"> — {sectorLabel(c)}</span>}
      </span>
      <MemberLine title="Πρόεδρος" names={c.president ? [c.president] : []} map={map} />
      <MemberLine title="Τακτικά" names={others} map={map} />
      <MemberLine title="Αναπληρωματικά" names={c.alternate} map={map} />
      {(c.excluded || []).length > 0 && (
        <span className="block text-xs leading-snug text-orange-800" data-testid="composition-excluded-line">
          <span className="text-slate-600">Εξαιρέθηκαν για τη δικάσιμο (Μονομελές ίδια ημέρα): </span>
          <span className="line-through font-medium">{c.excluded.join(", ")}</span>
        </span>
      )}
    </span>
  );
};

// «Ανάθεση σε Σύνθεση» — inline picker για πινάκιο × δικάσιμο Πολυμελούς (Πινάκια, Προσδιορισμός, διάλογος ορισμού δικαστή)
// onDone(composition | null) — η νέα σύνθεση (από το response) ή null μετά από αφαίρεση
export const CompositionPicker = ({ pinakio, dateGroup, onDone, defaultOpen = false, hideBadge = false }) => {
  const { user } = useAuth();
  const readOnly = isPresidentRole(user);
  const [open, setOpen] = useState(!!defaultOpen && !readOnly);
  const [options, setOptions] = useState([]);
  const [choice, setChoice] = useState("");
  const [busy, setBusy] = useState(false);
  const [prompt, setPrompt] = useState(null); // {conflicts:[{name, engagement}], body}
  const tid = `${pinakio.pinakio_code}-${dateGroup.date}`;
  const current = dateGroup.composition || null;

  useEffect(() => {
    if (!open) return;
    axios.get(`${API}/compositions/options`, { params: { court_type: "Πολυμελές", hearing_date: dateGroup.date } })
      .then(r => setOptions(r.data?.compositions || [])).catch(() => setOptions([]));
  }, [open, dateGroup.date]);

  const call = async (body) => {
    try {
      setBusy(true);
      const r = await axios.post(`${API}/hearings/composition`, {
        pinakio_label: pinakio.label, hearing_date: dateGroup.date, court_type: "Πολυμελές", ...body,
      });
      toast.success(r.data?.message || "Η σύνθεση ενημερώθηκε", { duration: 8000 });
      setOpen(false); setChoice(""); setPrompt(null);
      onDone?.(r.data?.composition || null);
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (detail && typeof detail === "object" && detail.code === "member_conflict") {
        setPrompt({ conflicts: detail.conflicts || [], body });
        return;
      }
      toast.error(typeof detail === "string" ? detail : (detail?.message || "Σφάλμα ανάθεσης σύνθεσης"), { duration: 12000 });
    } finally { setBusy(false); }
  };

  const clearComposition = () => {
    const ok = window.confirm(
      `Απαλοιφή της Σύνθεσης «${current?.label || ""}» από ΟΛΕΣ τις υποθέσεις του πινακίου «${pinakio.label}» για τη δικάσιμο ${dateGroup.date.split("-").reverse().join("/")};\n\n` +
      "Οι υποθέσεις θα μείνουν χωρίς Σύνθεση (ο Εισηγητής/Δικαστής Προδικαστικού δεν αλλάζει)."
    );
    if (ok) call({ clear: true });
  };

  if (!open) {
    return (
      <div className="flex items-center gap-1">
        {current && !hideBadge && (
          <Badge variant="outline" className="h-6 text-xs border-violet-400 text-violet-950 bg-violet-50 gap-1 max-w-[560px] truncate"
                 title={`${sectorLabel(current)}\n${compositionSummary(current)}`} data-testid={`composition-badge-${tid}`}>
            <Users className="w-3 h-3 shrink-0" />
            <span className="truncate">
              <b>Σύνθεση «{current.label}»</b>
              {sectorLabel(current) && <span> — {sectorLabel(current)}</span>}
              {current.president && <span> · Πρόεδρος: {current.president}</span>}
              {(current.excluded || []).length > 0 && <span className="text-orange-800"> · Εξαιρ.: {current.excluded.join(", ")}</span>}
            </span>
          </Badge>
        )}
        {!readOnly && (
        <Button variant="ghost" size="sm" className="h-7 text-xs gap-1 text-violet-700 hover:text-violet-900"
                onClick={() => setOpen(true)} data-testid={`composition-${tid}`}>
          <Users className="w-3.5 h-3.5" />{current ? "Αλλαγή Σύνθεσης" : "Ανάθεση σε Σύνθεση"}
        </Button>
        )}
        {current && !readOnly && (
          <Button variant="outline" size="sm" className="h-7 text-xs gap-1 border-red-500 text-red-700 hover:bg-red-50 font-semibold"
                  disabled={busy} onClick={clearComposition} title="Απαλοιφή της Σύνθεσης από όλες τις υποθέσεις της δικασίμου"
                  data-testid={`composition-clear-${tid}`}>
          {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <XCircle className="w-3.5 h-3.5" />}Απαλοιφή Σύνθεσης
          </Button>
        )}
      </div>
    );
  }

  const selected = options.find(o => `${o.sector_id}:${o.composition_index}` === choice);
  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex items-center gap-1 flex-wrap">
        <Select value={choice} onValueChange={(v) => { setChoice(v); setPrompt(null); }}>
          <SelectTrigger className="h-8 w-[440px] max-w-full text-sm text-slate-900" data-testid={`composition-select-${tid}`}>
            <SelectValue placeholder={options.length ? "Επιλέξτε σύνθεση…" : "Δεν υπάρχουν συνθέσεις στους τομείς Πολυμελούς"}>
              {selected ? `${selected.label} — ${sectorLabel(selected) || "Σύνθεση Πολυμελούς"}` : undefined}
            </SelectValue>
          </SelectTrigger>
          <SelectContent className="max-h-96 w-[560px] max-w-[95vw]" position="popper">
            {options.length === 0 && (
              <p className="px-3 py-2 text-xs text-amber-800" data-testid={`composition-empty-${tid}`}>
                Δεν έχουν οριστεί συνθέσεις στους τομείς Πολυμελούς (Διαχείριση → Τομείς Προδικαστικού → Συνθέσεις).
              </p>
            )}
            {options.map(o => (
              <SelectItem key={`${o.sector_id}:${o.composition_index}`} value={`${o.sector_id}:${o.composition_index}`}
                          className={`py-2 items-start border-b border-slate-100 last:border-0 ${(o.conflicts || []).length ? "bg-red-50/60" : ""}`}
                          data-testid={`composition-option-${o.sector_id}-${o.composition_index}`}>
                <CompositionDetails c={o} />
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button size="sm" className="h-8 text-xs bg-violet-700 hover:bg-violet-800" disabled={busy || !selected}
                onClick={() => call({ sector_id: selected.sector_id, composition_index: selected.composition_index })}
                data-testid={`composition-confirm-${tid}`}>
          {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : "Ανάθεση"}
        </Button>
        {current && (
          <Button size="sm" variant="ghost" className="h-8 text-xs text-red-600" disabled={busy} title="Απαλοιφή σύνθεσης"
                  onClick={clearComposition} data-testid={`composition-clear-inline-${tid}`}>
            <XCircle className="w-3 h-3" />
          </Button>
        )}
        <Button variant="ghost" size="sm" className="h-8 text-xs" onClick={() => { setOpen(false); setChoice(""); setPrompt(null); }}>&#x2715;</Button>
      </div>
      {selected && !prompt && (
        <div className="rounded border border-violet-300 bg-violet-50 px-3 py-2 max-w-[640px]">
          <CompositionDetails c={selected} testId={`composition-preview-${tid}`} />
        </div>
      )}
      {prompt && (
        <div className="rounded border border-red-300 bg-red-50 px-3 py-2 max-w-[640px] text-xs text-red-900 space-y-1.5"
             data-testid={`composition-conflict-prompt-${tid}`}>
          <p className="font-semibold flex items-center gap-1"><AlertTriangle className="w-3.5 h-3.5" />Προσοχή! Δικάσιμος Μονομελούς την ίδια ημερομηνία</p>
          <ul className="list-disc ml-4">
            {prompt.conflicts.map(c => <li key={c.name}><b>{c.name}</b> — {c.engagement}</li>)}
          </ul>
          <p>Να εξαιρεθεί/ούν από τη σύνθεση για τη συγκεκριμένη δικάσιμο; Η σύνθεση θα οριστεί κανονικά, αλλά ο/η δικαστής
            δεν θα μπορεί να οριστεί ως Δικαστής Έδρας ή Εισηγητής σε αυτή τη δικάσιμο.</p>
          <div className="flex gap-2">
            <Button size="sm" className="h-7 text-xs bg-red-700 hover:bg-red-800" disabled={busy}
                    onClick={() => call({ ...prompt.body, exclude_members: prompt.conflicts.map(c => c.name) })}
                    data-testid={`composition-exclude-confirm-${tid}`}>
              {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : "Ναι, εξαίρεση & ανάθεση"}
            </Button>
            <Button size="sm" variant="outline" className="h-7 text-xs" disabled={busy} onClick={() => setPrompt(null)}
                    data-testid={`composition-exclude-cancel-${tid}`}>Άκυρο</Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default CompositionPicker;
